import { format } from "date-fns";
import { PaperAirplaneIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  ArrowRepeat,
  Bell,
  Check,
  ChevronRight,
  Clock,
  ExclamationTriangle,
  Lock,
  SkipForward,
  X,
} from "react-bootstrap-icons";
import { formatMoney } from "../common";

const parseDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

// "7am" / "1pm" / "7:30am" — the send time in plain words, matching the header's schedule sentence.
const formatSendTime = (date) =>
  format(date, date.getMinutes() === 0 ? "haaa" : "h:mmaaa");

// "26 Jul at 9am" — a date + plain-words time, for a completed event on a (possibly different) day.
const formatDateTime = (date) => `${format(date, "d MMM")} at ${formatSendTime(date)}`;

// The request's display status → a coarse category that drives the marker, colour and connector.
const categoryOf = (status) => {
  switch (status) {
    case "Paid":
    case "Part-paid":
      return "paid";
    case "Refunded":
    case "Part-refund":
      return "refunded";
    case "Reminder sent":
      return "reminder";
    case "Processing":
      return "processing";
    case "Delivery failed":
      return "failed";
    case "Expired":
      return "expired";
    case "Cancelled":
      return "cancelled";
    case "Scheduled":
      return "scheduled";
    case "Skipped":
      return "skipped";
    // Sent / Delivered / Opened / Created — the request is out and live.
    default:
      return "sent";
  }
};

// Marker glyph + connector palette per category. `dot` is a plain marker (no icon); `strike` greys
// and strikes the row. A fresh send uses the brand primary/orange (matching the activity feed's
// "payment request sent"); a reminder keeps the brand amber #E7A918. The tile is a soft accent wash
// at half strength, while the connector `line` takes the darker icon colour.
// `highlight` is the focused-row tint — a lighter wash of the same colour as the row's marker.
const categoryStyles = {
  paid: { tile: "bg-green-100/50 text-green-600", Icon: Check, line: "bg-green-600", highlight: "bg-green-50/50" },
  refunded: { tile: "bg-green-100/50 text-green-600", Icon: Check, line: "bg-green-600", highlight: "bg-green-50/50" },
  reminder: { tile: "bg-[#E7A918]/10 text-[#E7A918]", Icon: PaperAirplaneIcon, line: "bg-[#E7A918]", highlight: "bg-[#E7A918]/5" },
  sent: { tile: "bg-primary-100/50 text-primary-600", Icon: PaperAirplaneIcon, line: "bg-primary-600", highlight: "bg-primary-50/50" },
  // A send that's still unpaid a day on reads as missed — red, with an alert marker.
  missed: { tile: "bg-red-100/50 text-red-600", Icon: ExclamationTriangle, line: "bg-red-600", highlight: "bg-red-50/50" },
  processing: { tile: "bg-blue-100/50 text-blue-600", Icon: ArrowRepeat, line: "bg-blue-600", highlight: "bg-blue-50/50" },
  failed: { tile: "bg-red-100/50 text-red-600", Icon: ExclamationTriangle, line: "bg-red-600", highlight: "bg-red-50/50" },
  expired: { tile: "bg-zinc-100/50 text-zinc-400", Icon: Clock, line: "dashed", highlight: "bg-zinc-50/50" },
  cancelled: { tile: "bg-zinc-100/50 text-zinc-400", Icon: X, line: "dashed", strike: true, highlight: "bg-zinc-50/50" },
  scheduled: { tile: "bg-zinc-100/50 text-zinc-400", dot: true, line: "dashed", highlight: "bg-zinc-50/50" },
  skipped: { tile: "bg-zinc-100/50 text-zinc-500", Icon: SkipForward, line: "dashed", highlight: "bg-zinc-50/50" },
};

// A sent/delivered instalment counts as missed once it's still unpaid this long after going out —
// a full day in production, but a minute in dev so the state can be exercised without waiting.
const MISSED_AFTER_MS = import.meta.env.DEV ? 60 * 1000 : 24 * 60 * 60 * 1000;

// dueOn is the instant the request was actually sent for an instalment that's gone out. A sent or
// reminded request still unpaid past the threshold counts as missed.
const isMissed = (instalment) => {
  const category = categoryOf(instalment.status);
  if (category !== "sent" && category !== "reminder") return false;
  const sentOn = parseDate(instalment.dueOn);
  return sentOn ? Date.now() - sentOn.getTime() > MISSED_AFTER_MS : false;
};

const categoryFor = (instalment) =>
  isMissed(instalment) ? "missed" : categoryOf(instalment.status);

const styleFor = (instalment) => categoryStyles[categoryFor(instalment)] ?? categoryStyles.scheduled;

const subtitleFor = (instalment, missed = false) => {
  const paidOn = parseDate(instalment.paidOn);
  const reminderOn = parseDate(instalment.reminderOn);
  // For an instalment that's gone out, dueOn is the instant the request was actually sent.
  const sentOn = parseDate(instalment.dueOn);
  // A missed send keeps its usual line and simply notes the payment is still outstanding.
  const unpaid = missed ? " but not yet paid" : "";
  switch (instalment.status) {
    case "Paid":
      return paidOn ? `Request paid ${formatDateTime(paidOn)}` : "Request paid";
    case "Part-paid":
      return "Partially paid";
    case "Refunded":
      return "Refunded";
    case "Part-refund":
      return "Partially refunded";
    case "Reminder sent":
      return (
        <span className="flex flex-col gap-0.5">
          <span>
            Payment request sent{sentOn ? ` at ${formatSendTime(sentOn)}` : ""}
            {unpaid}
          </span>
          <span className="inline-flex items-center gap-1">
            <Bell className="size-3 shrink-0 text-[#E7A918]" />
            Reminder sent{reminderOn ? ` ${formatDateTime(reminderOn)}` : ""}
          </span>
        </span>
      );
    case "Opened":
      return `Payment request opened${unpaid}`;
    case "Delivered":
      return `Payment request delivered${unpaid}`;
    case "Delivery failed":
      return "Delivery failed";
    case "Processing":
      return "Payment processing";
    case "Expired":
      return "Request expired";
    case "Cancelled":
      return "Request cancelled";
    case "Skipped":
      return "Not sent — the customer had already paid";
    case "Scheduled":
      return sentOn
        ? `Ontik will automatically send a payment request at ${formatSendTime(sentOn)}`
        : "Ontik will automatically send a payment request";
    // Sent / Created
    default:
      return `${sentOn ? `Payment request sent at ${formatSendTime(sentOn)}` : "Payment request sent"}${unpaid}`;
  }
};

// A segment of the connecting line above/below a marker, coloured by a row's status. The marker is
// a size-7 (28px, r=14px) circle centred on the title line at 26px from the row top; each segment
// stops ~2px short of it so the line runs between the circles rather than through them, and two
// segments meet at each row boundary for a continuous run.
const Connector = ({ category, half }) => {
  const line = (categoryStyles[category] ?? categoryStyles.scheduled).line;
  const pos = half === "top" ? "top-0 h-[10px]" : "top-[42px] bottom-0";
  const base = `pointer-events-none absolute left-1/2 -translate-x-1/2 ${pos}`;
  return line === "dashed" ? (
    <span className={`${base} border-l-2 border-dashed border-zinc-300`} />
  ) : (
    <span className={`${base} w-0.5 rounded-full ${line}`} />
  );
};

const InstalmentRow = ({ instalment, prevInstalment, focused, isFirst, isLast, onSelect }) => {
  const styles = styleFor(instalment);
  const { Icon } = styles;
  const dueOn = parseDate(instalment.dueOn);
  const struck = !!styles.strike;
  // A sent instalment has a payment request behind it — the row opens it, but only if the caller can
  // actually read that request (canView, resolved server-side). Otherwise it's shown locked, not
  // clickable, so we never open a drawer the API would 403.
  const hasRequest = !!instalment.paymentRequestId;
  const clickable = hasRequest && instalment.canView && !!onSelect;
  const restricted = hasRequest && !instalment.canView;
  const open = clickable ? () => onSelect(instalment.paymentRequestId) : undefined;

  return (
    <li
      className={`relative flex gap-4 rounded-xl px-3 ${focused ? styles.highlight : ""} ${
        clickable ? "cursor-pointer hover:bg-zinc-50" : ""
      }`}
      onClick={open}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                open();
              }
            }
          : undefined
      }
    >
      <div className="relative flex w-7 shrink-0 justify-center self-stretch">
        {!isFirst && prevInstalment && (
          <Connector category={categoryFor(prevInstalment)} half="top" />
        )}
        {!isLast && <Connector category={categoryFor(instalment)} half="bottom" />}
        <span
          className={`relative z-10 mt-3 flex size-7 items-center justify-center rounded-full ${styles.tile}`}
        >
          {styles.dot ? (
            <span className="size-3.5 rounded-full bg-current" />
          ) : (
            <Icon className="size-3.5" />
          )}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 items-start justify-between gap-4 py-4">
        <div className="min-w-0">
          <p
            className={`text-sm font-semibold ${
              struck ? "text-zinc-400 line-through" : "text-zinc-900"
            }`}
          >
            {dueOn ? format(dueOn, "EEE, d MMM yyyy") : "—"}
            {instalment.total > 1 ? ` · ${instalment.number} of ${instalment.total}` : ""}
          </p>
          <p className="mt-1 text-sm text-zinc-500">
            {subtitleFor(instalment, isMissed(instalment))}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <p
            className={`text-sm font-semibold ${
              struck ? "text-zinc-400 line-through" : "text-zinc-900"
            }`}
          >
            {formatMoney(instalment.amount) ?? ""}
          </p>
          {clickable && <ChevronRight className="size-3.5 text-zinc-400" />}
          {restricted && (
            <span title="You don't have access to this payment request">
              <Lock className="size-3.5 text-zinc-300" />
            </span>
          )}
        </div>
      </div>
    </li>
  );
};

/**
 * A promise to pay is a single send, so it renders as one card rather than a timeline. Before it
 * goes out it mirrors the plan header box — handshake tile, the schedule sentence, and a bin to
 * cancel. Once sent it reads like an instalment row (amount + chevron, opens the request), but keeps
 * the promise-to-pay handshake as the primary glyph with the request's status as a small corner
 * badge instead of swapping the whole marker.
 */
export const PromiseToPayCard = ({
  instalment,
  identity,
  onSelectPaymentRequest,
  onRequestCancel,
  canCancel,
  cancelLabel,
  amountLabel,
}) => {
  const { Icon: IdentityIcon, tile: identityTile } = identity;
  const dueOn = parseDate(instalment.dueOn);
  // A statement/bucket send carries no fixed figure (resolved live at pay time) — name the bucket
  // ("Overdue invoices") rather than showing "£0".
  const money = instalment.amount != null ? formatMoney(instalment.amount) : (amountLabel ?? null);
  const when = dueOn ? format(dueOn, "EEE, d MMM yyyy") : "—";

  if (instalment.status === "Scheduled") {
    return (
      <div className="flex items-center gap-3.5 rounded-2xl border border-zinc-200 p-4">
        <div
          className={`flex size-10 shrink-0 items-center justify-center rounded-md ${identityTile}`}
        >
          <IdentityIcon className="size-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-semibold text-zinc-900">{when}</p>
          <p className="text-sm text-zinc-600">
            Automatically send {money ? `${money} ` : ""}payment request
            {dueOn ? ` at ${formatSendTime(dueOn)}` : ""}
          </p>
        </div>
        {canCancel && (
          <button
            type="button"
            onClick={onRequestCancel}
            title={`Cancel ${cancelLabel.toLowerCase()}`}
            className="shrink-0 rounded-md p-1 text-red-500 hover:bg-red-50 hover:text-red-600"
          >
            <span className="sr-only">Cancel {cancelLabel.toLowerCase()}</span>
            <TrashIcon className="size-5" />
          </button>
        )}
      </div>
    );
  }

  const status = styleFor(instalment);
  const missed = isMissed(instalment);
  const struck = !!status.strike;
  const StatusIcon = status.Icon;
  const hasRequest = !!instalment.paymentRequestId;
  const clickable = hasRequest && instalment.canView && !!onSelectPaymentRequest;
  const restricted = hasRequest && !instalment.canView;
  const open = clickable
    ? () => onSelectPaymentRequest(instalment.paymentRequestId)
    : undefined;

  return (
    <div
      className={`flex items-center gap-3.5 rounded-2xl border border-zinc-200 p-4 ${
        clickable ? "cursor-pointer hover:bg-zinc-50" : ""
      }`}
      onClick={open}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                open();
              }
            }
          : undefined
      }
    >
      <div className="relative shrink-0">
        <div
          className={`flex size-10 items-center justify-center rounded-md ${identityTile}`}
        >
          <IdentityIcon className="size-5" />
        </div>
        <span
          className={`absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full ring-2 ring-white ${status.tile}`}
        >
          {status.dot ? (
            <span className="size-1.5 rounded-full bg-current" />
          ) : (
            StatusIcon && <StatusIcon className="size-2.5" />
          )}
        </span>
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <p
          className={`text-sm font-semibold ${
            struck ? "text-zinc-400 line-through" : "text-zinc-900"
          }`}
        >
          {when}
        </p>
        <p className="text-sm text-zinc-500">{subtitleFor(instalment, missed)}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <p
          className={`text-sm font-semibold ${
            struck ? "text-zinc-400 line-through" : "text-zinc-900"
          }`}
        >
          {money ?? ""}
        </p>
        {clickable && <ChevronRight className="size-4 text-zinc-400" />}
        {restricted && (
          <span title="You don't have access to this payment request">
            <Lock className="size-4 text-zinc-300" />
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * The per-instalment timeline for a payment plan's detail modal: one row per scheduled send, each
 * showing the payment request's real status (paid, reminder sent, cancelled, expired, delivery
 * failed, …) or "Scheduled" for a future one. Presentational — the modal owns the fetch. A row with
 * a payment request behind it calls `onSelectPaymentRequest` to open that request.
 */
const PlanInstalmentTimeline = ({
  instalments = [],
  onSelectPaymentRequest,
  focusInstalment = null,
}) => {
  if (instalments.length === 0) return null;

  return (
    <div className="rounded-2xl border border-zinc-200 p-5">
      <ol>
        {instalments.map((instalment, index) => (
          <InstalmentRow
            key={instalment.number}
            instalment={instalment}
            prevInstalment={index > 0 ? instalments[index - 1] : null}
            focused={instalment.number === focusInstalment}
            isFirst={index === 0}
            isLast={index === instalments.length - 1}
            onSelect={onSelectPaymentRequest}
          />
        ))}
      </ol>
    </div>
  );
};

export default PlanInstalmentTimeline;
